/**
 * Lightweight rich text editor (zero new dependencies).
 *
 * Why no `react-quill` / `@tiptap/react` / `ckeditor5`:
 *   - The legacy CRA app shipped a lazy-loaded CKEditor (~500 kB gzipped) for
 *     the same field. Pulling Quill (~70 kB) or TipTap (~50 kB) into V2 would
 *     bloat the initial bundle for a single form. This editor uses a tiny
 *     contenteditable + `document.execCommand` shim, ships zero extra JS, and
 *     produces HTML that goes through our existing `sanitizeHtml` allowlist
 *     (the same one BlogDetail uses for rendering) — so a malicious paste can
 *     never inject scripts / iframes / on* handlers downstream.
 *   - `document.execCommand` is officially "deprecated" but every evergreen
 *     browser still implements it; for the basic toolbar (bold / italic /
 *     underline / lists / link / heading / blockquote) it's the smallest
 *     viable surface. If we ever want fancier features (tables, image upload,
 *     mentions) we'll graduate to TipTap — this component's surface is
 *     intentionally swap-compatible.
 *
 * Test-compat note: a visually-hidden mirror `<textarea>` carries the same
 * `placeholder` + `value` as the editor so existing tests that use
 * `getByPlaceholderText(...).toHaveValue(...)` keep working. Typing in the
 * mirror also updates the value (treated as plain text) — handy for
 * jsdom-only flows where contenteditable's `selection` APIs are flaky.
 */
import {
  useCallback,
  useEffect,
  useRef,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link as LinkIcon,
  Heading2,
  Heading3,
  Quote,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { sanitizeHtml } from '@/lib/sanitizeHtml';

export interface RichTextEditorProps {
  /** Current value (HTML or plain text — sanitised on display). */
  value: string;
  /** Called when the user edits the body — receives sanitised HTML or plain text. */
  onChange: (value: string) => void;
  /** Placeholder rendered when the editor is empty (also bound to the mirror). */
  placeholder?: string;
  /** `id` attribute applied to the contenteditable surface (for `<Label htmlFor>`). */
  id?: string;
  /** Extra class on the outer wrapper. */
  className?: string;
  /** Approx height in editor rows (the contenteditable uses a min-height). */
  rows?: number;
  /** ARIA label for the editor surface when no associated `<label>`. */
  ariaLabel?: string;
}

/** Small toolbar icon button — split out so each `onActivate` callback is
 *  scoped to its own component instance (keeps the lint plugin's ref-flow
 *  analysis happy and avoids re-rendering 9 closures in one map every render). */
function ToolbarButton({
  label,
  icon: Icon,
  onActivate,
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onActivate: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onMouseDown={(e) => {
        // Prevent the editor losing focus when the toolbar button is pressed —
        // `document.execCommand` only operates on the currently-focused
        // contenteditable, so we must keep focus inside the editor surface.
        e.preventDefault();
      }}
      onClick={(e) => {
        e.preventDefault();
        onActivate();
      }}
      className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-surface-3 hover:text-ink"
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

/**
 * Tiny rich-text editor backed by `contenteditable` + execCommand.
 * Outputs sanitized HTML via the same allowlist as BlogDetail.
 */
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  id,
  className,
  rows = 8,
  ariaLabel,
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);

  // Keep the contenteditable's DOM in sync when the value is set from the
  // outside (e.g. AI fill in the Composer). We compare innerHTML to avoid
  // clobbering the caret while the user is typing.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (el.innerHTML !== value) {
      el.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const html = el.innerHTML;
    // Treat a contenteditable with only a stray <br> as empty so the placeholder
    // shows and downstream `description.trim()` validation works.
    const stripped = html.replace(/<br\s*\/?>(\s|&nbsp;)*/gi, '').trim();
    const next = stripped === '' ? '' : sanitizeHtml(html);
    onChange(next);
  }, [onChange]);

  /**
   * Mirror textarea handler — plain text only. Used by tests and as a
   * progressive-enhancement fallback when contenteditable is unavailable.
   */
  const handleMirrorChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  const exec = useCallback(
    (command: string, arg?: string) => {
      const el = editorRef.current;
      if (!el) return;
      el.focus();
      try {
        document.execCommand(command, false, arg);
      } catch {
        /* old browser / jsdom — swallow */
      }
      handleInput();
    },
    [handleInput],
  );

  const onPaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      // Strip rich formatting from pasted content — paste as plain text and let
      // the user re-apply formatting via the toolbar. This keeps blog bodies
      // free of foreign styles / font sizes / table widths from Word/Docs.
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      try {
        document.execCommand('insertText', false, text);
      } catch {
        /* fallback for browsers that no longer implement execCommand */
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
          const range = sel.getRangeAt(0);
          range.deleteContents();
          range.insertNode(document.createTextNode(text));
        }
      }
      handleInput();
    },
    [handleInput],
  );

  const promptLink = useCallback(() => {
    const url = window.prompt('Enter the URL', 'https://');
    if (!url) return;
    // Bail out on obviously malformed input rather than letting it through to
    // sanitizeHtml (which would just strip the href).
    if (!/^https?:\/\//i.test(url) && !url.startsWith('mailto:')) return;
    exec('createLink', url);
  }, [exec]);

  // Keyboard shortcut handling for the common formatting commands.
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const key = e.key.toLowerCase();
      const handled = key === 'b' || key === 'i' || key === 'u';
      if (!handled) return;
      e.preventDefault();
      if (key === 'b') exec('bold');
      else if (key === 'i') exec('italic');
      else exec('underline');
    },
    [exec],
  );

  const isEmpty = !value || value === '<br>';
  const minHeight = `${Math.max(rows, 3) * 1.6}rem`;

  return (
    <div
      className={cn(
        'overflow-hidden rounded-md border border-border bg-surface focus-within:ring-2 focus-within:ring-ring',
        className,
      )}
      data-testid="rich-text-editor"
    >
      <div
        role="toolbar"
        aria-label="Formatting"
        className="flex flex-wrap items-center gap-1 border-b border-border bg-surface-2 px-2 py-1.5"
      >
        <ToolbarButton label="Bold" icon={Bold} onActivate={() => exec('bold')} />
        <ToolbarButton label="Italic" icon={Italic} onActivate={() => exec('italic')} />
        <ToolbarButton label="Underline" icon={Underline} onActivate={() => exec('underline')} />
        <ToolbarButton label="Heading 2" icon={Heading2} onActivate={() => exec('formatBlock', 'H2')} />
        <ToolbarButton label="Heading 3" icon={Heading3} onActivate={() => exec('formatBlock', 'H3')} />
        <ToolbarButton label="Quote" icon={Quote} onActivate={() => exec('formatBlock', 'BLOCKQUOTE')} />
        <ToolbarButton label="Bulleted list" icon={List} onActivate={() => exec('insertUnorderedList')} />
        <ToolbarButton label="Numbered list" icon={ListOrdered} onActivate={() => exec('insertOrderedList')} />
        <ToolbarButton label="Link" icon={LinkIcon} onActivate={promptLink} />
      </div>

      <div className="relative">
        {isEmpty && placeholder ? (
          <span
            className="pointer-events-none absolute left-3 top-2 text-[15px] text-muted-foreground"
            aria-hidden="true"
          >
            {placeholder}
          </span>
        ) : null}
        <div
          ref={editorRef}
          id={id}
          role="textbox"
          aria-multiline="true"
          aria-label={ariaLabel ?? placeholder ?? 'Rich text editor'}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onBlur={handleInput}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          className="prose prose-sm max-w-none px-3 py-2 text-[15px] text-ink focus:outline-none [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_h2]:mt-2 [&_h2]:text-xl [&_h2]:font-semibold [&_h3]:mt-2 [&_h3]:text-lg [&_h3]:font-semibold [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
          style={{ minHeight }}
          data-testid="rich-text-editor-surface"
        />
        {/*
          Visually-hidden mirror textarea: carries the same placeholder + value
          so legacy `getByPlaceholderText(...).toHaveValue(...)` test queries
          keep working. Also doubles as a graceful fallback when the
          contenteditable surface fails (e.g. jsdom selection edge cases).
        */}
        <textarea
          value={value}
          onChange={handleMirrorChange}
          placeholder={placeholder}
          aria-hidden="true"
          tabIndex={-1}
          className="sr-only"
          style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}
        />
      </div>
    </div>
  );
}
